import * as z from 'zod';
import { FIRST_PAGE } from '@noor/config';
import {
  DEFAULT_CANCELLED_ROLE_TAB,
  DEFAULT_EQUIPMENT_TAB,
  DEFAULT_STAGE_TAB,
} from './stages';

/**
 * Orders list tabs + URL search params. The 11 tabs (with the deliberate
 * backend misspelling `SHCHEDULED`) are the cpanel contract; each maps to a
 * `count_info` key for its badge and expands into `is_*` request flags via
 * `buildOrdersListFilter` (see `lib/build-list-filter.ts`).
 */

export const ORDER_TABS = [
  { value: 'ALL', countKey: 'total' },
  { value: 'SHCHEDULED', countKey: 'scheduled' },
  { value: 'SEARCH', countKey: 'in_search' },
  { value: 'RETURNED', countKey: 'returned' },
  { value: 'NOT-FOUND', countKey: 'not_found' },
  { value: 'CURRENT', countKey: 'active' },
  { value: 'COMPLETED', countKey: 'finished' },
  { value: 'CANCELLED', countKey: 'cancelled' },
  { value: 'NEG_BALANCE', countKey: 'cancelled_with_payment' },
  { value: 'is_paid', countKey: 'not_paid_count' },
  { value: 'is_courier_debt', countKey: 'debt_count' },
] as const;

export const orderTabSchema = z.enum([
  'ALL',
  'SHCHEDULED',
  'SEARCH',
  'RETURNED',
  'NOT-FOUND',
  'CURRENT',
  'COMPLETED',
  'CANCELLED',
  'NEG_BALANCE',
  'is_paid',
  'is_courier_debt',
]);
export type OrderTab = z.infer<typeof orderTabSchema>;

/** Order list source: regular orders vs the fridge/market list. */
export const orderListTypeSchema = z.enum(['orders', 'market']);
export type OrderListType = z.infer<typeof orderListTypeSchema>;

/** Boolean toggle filters (cpanel `FILTERS`: Рынок / Интеграция / Бизнес). */
export const ORDER_TOGGLE_FILTERS = [
  { key: 'is_market', param: 'isMarket', labelKey: 'toggle.market' },
  { key: 'is_integration', param: 'isIntegration', labelKey: 'toggle.integration' },
  { key: 'is_business', param: 'isBusiness', labelKey: 'toggle.business' },
] as const;

export type OrderToggleKey = (typeof ORDER_TOGGLE_FILTERS)[number]['key'];
export type OrderToggleParam = (typeof ORDER_TOGGLE_FILTERS)[number]['param'];

/** Stage-band sub-tab values (`current_status_tab`) under the CURRENT tab. */
export const orderStageTabSchema = z.enum([
  'to_origin',
  'wait_order',
  'to_destination',
  'wait_receiver',
]);

/**
 * Equipment sub-tabs under the SEARCH tab. `all` carries no scope; `scooter` /
 * `car` are sent as `equipment_scope`.
 */
export const orderEquipmentTabSchema = z.enum(['all', 'scooter', 'car']);

/** Who-cancelled sub-tabs under CANCELLED. `all` omits `cancelled_by_roles`. */
export const orderCancelledRoleTabSchema = z.enum(['all', 'VENDOR', 'USER']);

/**
 * ID-column sort (cpanel `CustomHeader` positions): `asc` = position 1 →
 * `sort_by_late_orders=true`, `desc` = position 2 → `sort_by_late_orders=false`,
 * absent = position 0 → the key is omitted.
 */
export const orderIdSortSchema = z.enum(['asc', 'desc']);
export type OrderIdSort = z.infer<typeof orderIdSortSchema>;

/**
 * Default page size for the orders tables — cpanel's `tableLimit` default
 * (100). Every orders request carries it on first open, like cpanel.
 */
export const ORDERS_DEFAULT_PAGE_SIZE = 100;

/**
 * URL search-param schema for the orders lists. The route's `validateSearch`
 * uses this, so the URL is the single source of truth for tab/filter/sort/page.
 * All fields optional with defaults (or `.catch`) so a bare or stale `/orders`
 * URL resolves cleanly instead of throwing.
 */
export const orderListSearchSchema = z.object({
  tab: orderTabSchema.catch('ALL').default('ALL'),
  page: z.coerce.number().int().positive().catch(FIRST_PAGE).default(FIRST_PAGE),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .catch(ORDERS_DEFAULT_PAGE_SIZE)
    .default(ORDERS_DEFAULT_PAGE_SIZE),
  search: z.string().optional().catch(undefined),
  /** Day bounds (`YYYY-MM-DD`); unset = today (the cpanel default range). */
  startDate: z.string().optional().catch(undefined),
  endDate: z.string().optional().catch(undefined),
  vendorId: z.union([z.string(), z.number()]).optional().catch(undefined),
  /**
   * Set only by a URL-seeded client filter (cpanel `client_id` deep link):
   * the counts endpoints then send `client_id` instead of `vendor_id`.
   */
  clientType: z.enum(['client', 'vendor']).optional().catch(undefined),
  courierId: z.union([z.string(), z.number()]).optional().catch(undefined),
  idSort: orderIdSortSchema.optional().catch(undefined),
  /** Stage band within CURRENT (`current_status_tab`). */
  stageTab: orderStageTabSchema.catch(DEFAULT_STAGE_TAB).default(DEFAULT_STAGE_TAB),
  /** Equipment sub-tab within SEARCH (`equipment_scope`). */
  equipmentTab: orderEquipmentTabSchema
    .catch(DEFAULT_EQUIPMENT_TAB)
    .default(DEFAULT_EQUIPMENT_TAB),
  /** Who-cancelled sub-tab within CANCELLED (`cancelled_by_roles`). */
  cancelledRole: orderCancelledRoleTabSchema
    .catch(DEFAULT_CANCELLED_ROLE_TAB)
    .default(DEFAULT_CANCELLED_ROLE_TAB),
  isMarket: z.boolean().optional().catch(undefined),
  isIntegration: z.boolean().optional().catch(undefined),
  isBusiness: z.boolean().optional().catch(undefined),
});
export type OrderListSearch = z.infer<typeof orderListSearchSchema>;

/**
 * cpanel deep-link keys (`order_id`, `status`, `courier_id`, `client_id`,
 * `vendor_id`) the old orders page consumed from its URL. Normalized into the
 * camelCase schema before validation so links built against cpanel still land
 * on the right tab/filter. `order_id` is surfaced separately — the route
 * redirects it to the detail page (cpanel opened its drawer).
 */
export function normalizeLegacyOrderSearch(
  search: Record<string, unknown>,
): Record<string, unknown> {
  const {
    order_id: _orderId,
    status,
    courier_id: courierId,
    client_id: clientId,
    vendor_id: vendorId,
    ...rest
  } = search;
  void _orderId;
  const next: Record<string, unknown> = { ...rest };
  if (status !== undefined && rest.tab === undefined) next.tab = status;
  if (courierId !== undefined && rest.courierId === undefined) next.courierId = courierId;
  const legacyClient = clientId ?? vendorId;
  if (legacyClient !== undefined && rest.vendorId === undefined) {
    next.vendorId = legacyClient;
    next.clientType = clientId !== undefined ? 'client' : 'vendor';
  }
  return next;
}

/**
 * The snake_case request filter sent to `GET /admin/orders`. Index signature
 * because the real backend accepts a wider set than we name here (cpanel's
 * `OrderListFilter` does the same).
 */
export interface OrderListFilter {
  display_id?: string | undefined;
  limit?: string | number | undefined;
  skip?: string | number | undefined;
  vendor_id?: string | number | undefined;
  courier_id?: string | number | undefined;
  from_date?: string | undefined;
  till_date?: string | undefined;
  status_id_order?: string | undefined;
  sort_by_late_orders?: boolean | undefined;
  /** Stage band within the active list; sent only when `is_active` is true. */
  current_status_tab?: string | undefined;
  /** In-search equipment narrowing; sent only alongside `is_in_search`. */
  equipment_scope?: string | undefined;
  /** Who-cancelled narrowing; sent only alongside `is_cancelled`. */
  cancelled_by_roles?: string | undefined;
  is_active?: boolean | undefined;
  is_scheduled?: boolean | undefined;
  is_finished?: boolean | undefined;
  is_cancelled?: boolean | undefined;
  is_not_found?: boolean | undefined;
  is_returned?: boolean | undefined;
  is_in_search?: boolean | undefined;
  is_cancelled_with_balance?: boolean | undefined;
  is_paid?: boolean | undefined;
  is_courier_debt?: boolean | undefined;
  is_market?: boolean | undefined;
  is_integration?: boolean | undefined;
  is_business?: boolean | undefined;
  [key: string]: unknown;
}
